import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Branding from "../../config/Branding";
// Note: Requires `recharts` package. Run: npm install recharts
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from "recharts";

// Work around TS/react typings mismatch by aliasing components as any for JSX usage
const RESP: any = ResponsiveContainer as any;
const RC: any = RadarChart as any;
const PG: any = PolarGrid as any;
const PAA: any = PolarAngleAxis as any;
const PRA: any = PolarRadiusAxis as any;

const RD: any = Radar as any;

export type FactorsRadarAxis = {
    label: string;
    value: number; // current value (raw)
    minimum: number;
    maximum: number;
};

// Custom tick renderer: trim "weight", small font, split into up to three lines
const renderAngleTick = (props: any) => {
    const { payload, x, y, textAnchor } = props;
    const raw = String(payload?.value ?? payload?.payload?.subject ?? "");
    const trimmed = raw.replace(/\s*weight$/i, "").trim();
    const words = trimmed.split(/\s+/);

    // Build up to 3 lines attempting to balance words
    const lines: string[] = [];
    for (let i = 0; i < words.length; i++) {
        if (lines.length === 0) {
            lines.push(words[i]);
        } else if (lines.length < 3 && (lines[lines.length - 1].length + 1 + words[i].length) <= 12) {
            lines[lines.length - 1] = `${lines[lines.length - 1]} ${words[i]}`;
        } else if (lines.length < 3) {
            lines.push(words[i]);
        } else {
            lines[lines.length - 1] = `${lines[lines.length - 1]} ${words[i]}`;
        }
    }

    // Nudge short labels like "Leaving Soon" to two lines for compactness
    if (/^leaving\s+soon$/i.test(trimmed)) {
        return (
            <text x={x} y={y} textAnchor={textAnchor} fill={Branding.white} style={{ pointerEvents: "none" }}>
                <tspan fontSize={10} x={x} dy="0em">Leaving</tspan>
                <tspan fontSize={10} x={x} dy="1.05em">Soon</tspan>
            </text>
        );
    }


    return (
        <text x={x} y={y} textAnchor={textAnchor} fill={Branding.white} style={{ pointerEvents: "none" }}>
            {lines.map((ln, idx) => (
                <tspan key={idx} fontSize={10} x={x} dy={idx === 0 ? "0em" : "1.05em"}>{ln}</tspan>
            ))}
        </text>
    );
};

type FactorsRadarProps = {
    axes: FactorsRadarAxis[];
    height?: number;
};

const ChartContainer = styled.div`
    width: 100%;
    height: ${(p: { $height?: number }) => `${p.$height ?? 260}px`};
    background: ${Branding.transparentBackground};
    border: none; /* remove border around factor summary */
    border-radius: 8px;
    padding: 6px 8px 22px 8px; /* more bottom padding */
    margin-bottom: 18px; /* extra space below the radar */
`;

const FactorsRadar: React.FC<FactorsRadarProps> = ({ axes, height = 220 }) => {
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        // Small delay to ensure the component is properly mounted and sized
        const timer = setTimeout(() => setIsMounted(true), 50);
        return () => clearTimeout(timer);
    }, []);

    if (!axes || axes.length === 0 || !isMounted) return null;

    const data = axes.map((a) => {
        const range = Math.max(0.00001, a.maximum - a.minimum);
        const norm = Math.max(0, Math.min(1, (a.value - a.minimum) / range));
        const v = Math.round(norm * 100) + 5; // display bias so zero slider still renders ~5
        return {
            subject: a.label,
            value: Math.max(0, Math.min(105, v)),
            fullMark: 105
        };
    });

    const containerHeight = height + 60;

    return (
        <ChartContainer $height={containerHeight}>
            <RESP width="100%" height={containerHeight} minHeight={containerHeight}>
                <RC data={data} cx="50%" cy="48%" outerRadius="95%" margin={{ top: 10, right: 10, bottom: 56, left: 10 }}>
                    <PG />
                    <PAA dataKey="subject" tick={renderAngleTick} tickLine={false} stroke={Branding.white} />
                    <PRA domain={[0, 105]} tick={false} tickCount={6} axisLine={false} />
                    <RD name="Factors" dataKey="value" stroke={Branding.twentyfouriLime} fill={Branding.twentyfouriLime} fillOpacity={0.5} />
                </RC>
            </RESP>
        </ChartContainer>
    );
};

export default FactorsRadar;
